<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { usePortalsStore } from '@/stores/portals'
import { getFileUrl } from '@/utils/file'
import optoroLogo from '@/assets/images/optoroLogo.png'

const { selectedPortal } = storeToRefs(usePortalsStore())

const showSitewideMessage = computed(() => {
  const { fields } = selectedPortal.value
  return fields.customize.content.sitewide.$enabled.inputValue && fields.customize.content.sitewide.$message.inputValue?.length
})

const logoUrl = computed(() => {
  const { fields } = selectedPortal.value
  if (fields.customize.globalStyles.logoAssets.$mainLogo.inputValue) {
    return getFileUrl(fields.customize.globalStyles.logoAssets.$mainLogo.inputValue)
  }
  return null
})

const bodyFontFamily = computed(() => {
  const { fields } = selectedPortal.value
  return fields.customize.globalStyles.fonts.$bodyBrandFontFamily.inputValue
    ? fields.customize.globalStyles.fonts.$bodyBrandFontFamily.inputValue
    : fields.customize.globalStyles.fonts.$bodyWebFontFamily.inputValue
})

const poweredByText = computed(() => (
  selectedPortal.value?.values?.settings?.strings?.FOOTER?.POWERED_BY
))

const termsOfServiceText = computed(() => (
  selectedPortal.value?.values?.settings?.strings?.FOOTER?.TERMS_OF_SERVICE
))

const privacyPolicyText = computed(() => (
  selectedPortal.value?.values?.settings?.strings?.FOOTER?.PRIVACY_POLICY
))

const headerBackgroundColor = computed(() => {
  return selectedPortal.value.fields.customize.globalStyles.colors.$headerBackgroundColor.inputValue
})

const footerBackgroundColor = computed(() => {
  return selectedPortal.value.fields.customize.globalStyles.colors.$footerBackgroundColor.inputValue
})

const dividerBarColor = computed(() => {
  return selectedPortal.value.fields.customize.globalStyles.colors.$dividerBarColor.inputValue
})

</script>

<template>
  <div
    class="main-layout-preview d-flex flex-column"
    :style="{
      'font-family': bodyFontFamily,
      'color': selectedPortal.fields.customize.globalStyles.fonts.$bodyColor.inputValue
    }"
  >
    <div
      v-if="showSitewideMessage"
      class="sitewide-message"
      :style="{
        'background-color': selectedPortal.fields.customize.content.sitewide.$backgroundColor.inputValue,
        'color': selectedPortal.fields.customize.content.sitewide.$textColor.inputValue
      }"
    >
      {{ selectedPortal.fields.customize.content.sitewide.$message.inputValue }}
    </div>
    <slot name="header">
      <div class="header" 
        :style="{
          'background-color': headerBackgroundColor,
          'border-bottom-color': dividerBarColor
        }">
        <img class="img-fluid" v-if="logoUrl" :src="logoUrl" alt="">
      </div>
    </slot>
    <div class="flex-fill d-flex flex-column">
      <slot></slot>
    </div>
    <div class="footer" 
      :style="{
        'background-color': footerBackgroundColor,
        'border-top-color': dividerBarColor
      }">
      <div class="d-flex align-items-center">
        {{ poweredByText }}&nbsp;
        <img
          :src="optoroLogo"
          class="img-fluid optoro-logo"
          alt="Optoro"
        >
      </div>
      <div>
        <span class="me-2">{{ termsOfServiceText }}</span>
        <span class="ms-2">{{ privacyPolicyText }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.main-layout-preview {
  width: 100%;
  background-color: #FFFFFF;
  border: 1px solid #DBDCDD;
  font-family: sans-serif;

  .sitewide-message {
    padding: 10px;
    font-size: 16px;
    font-weight: 700;
    text-align: center;
    background-color: #6D6D6D;
    color: white;
  }

  .header {
    padding-top: 1.6rem;
    padding-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid #DBDCDD;
    height: 4.5em;

    img {
      max-height: 50px;
      max-width: 200px;
    }
  }

  .footer {
    border-top: 1px solid #DBDCDD;
    width: 100%;
    height: 80px;
    font-size: 12px;
    color: #919191;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .optoro-logo {
      max-width: 50px;
    }
  }
}
</style>
